import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownToLine, ArrowUpFromLine, Radio, X } from 'lucide-react';
import type { Equipment, HydraulicLink, IrrigationBlock, Sensor } from '../../types/digitalTwin';
import type { EquipmentTelemetry, DemoAssumption } from '../../lib/digitalTwinTelemetry';
import { useIntlFormatting } from '../../utils/intl';

type Selection = { kind: 'equipment'; value: Equipment } | { kind: 'block'; value: IrrigationBlock };

const ValueList: React.FC<{ title: string; value: Record<string, unknown> }> = ({ title, value }) => {
  const entries = Object.entries(value || {});
  if (!entries.length) return null;
  return (
    <section className="dt-detail-section">
      <h4>{title}</h4>
      <dl>{entries.map(([key, item]) => (
        <React.Fragment key={key}>
          <dt>{key.replace(/_/g, ' ')}</dt>
          <dd>{typeof item === 'object' ? JSON.stringify(item) : String(item)}</dd>
        </React.Fragment>
      ))}</dl>
    </section>
  );
};

const BlockDetails: React.FC<{ block: IrrigationBlock }> = ({ block }) => {
  const { t } = useTranslation('digitalTwin');
  const values: Array<[string, unknown]> = [
    [t('surface'), block.surface_ha != null ? `${block.surface_ha} ha` : '—'],
    [t('crop'), block.crop_id || '—'],
    [t('variety'), block.variety_id || '—'],
    [t('irrigation'), block.irrigation_type],
    [t('lines'), block.line_count],
    [t('emittersPerLine'), block.emitters_per_line],
    [t('emitterFlow'), `${block.emitter_flow_l_h} l/h`],
    [t('theoreticalFlow'), `${block.theoretical_flow_m3_h ?? 0} m³/h`],
    [t('nominalPressure'), block.nominal_pressure_bar != null ? `${block.nominal_pressure_bar} bar` : '—'],
    [t('soil'), block.soil_type || '—'],
    [t('soilTexture'), block.soil_texture || '—'],
    [t('rootDepth'), block.root_depth_cm != null ? `${block.root_depth_cm} cm` : '—'],
  ];
  return <dl className="dt-detail-grid">{values.map(([label, value]) => (
    <React.Fragment key={label}><dt>{label}</dt><dd>{String(value)}</dd></React.Fragment>
  ))}</dl>;
};

const TelemetryDetails: React.FC<{ telemetry?: EquipmentTelemetry }> = ({ telemetry }) => {
  const { t } = useTranslation('digitalTwin');
  const { formatDateTime } = useIntlFormatting();
  return (
    <section className="dt-detail-section">
      <h4>{t('telemetry')}</h4>
      {!telemetry ? <p className="dt-muted">{t('noTelemetry')}</p> : (
        <>
          <dl className="dt-detail-grid">
            <dt>{t('source')}</dt><dd>{t(`sourceState.${telemetry.source}`, telemetry.source)}</dd>
            <dt>{t('status')}</dt><dd>{t(`statusLabel.${telemetry.statusKey}`, telemetry.statusKey)}</dd>
            <dt>{t('activity')}</dt><dd>{telemetry.active == null ? t('unknown') : telemetry.active ? t('active') : t('inactive')}</dd>
            <dt>{t('alert')}</dt><dd>{telemetry.alert ? `${t(`alertSeverity.${telemetry.alert.severity}`, telemetry.alert.severity)}: ${telemetry.alert.message}` : t('noAlert')}</dd>
            {telemetry.pressureState && <><dt>{t('pressure')}</dt><dd>{t(`pressureState.${telemetry.pressureState}`, telemetry.pressureState)}</dd></>}
            {telemetry.waterDeliveryEstimate && <><dt>{t('waterDelivery')}</dt><dd>{t(telemetry.waterDeliveryEstimate.key, telemetry.waterDeliveryEstimate.params as Record<string, unknown>)}</dd></>}
            <dt>{t('lastUpdated')}</dt><dd>{telemetry.lastUpdated ? formatDateTime(new Date(telemetry.lastUpdated)) : t(`statusLabel.${telemetry.fallbackKey}`, telemetry.fallbackKey)}</dd>
            {telemetry.sourceNodeName && <><dt>{t('sourceNode')}</dt><dd>{telemetry.sourceNodeName}</dd></>}
          </dl>
          {telemetry.source === 'demo' && <p className="dt-demo-note">{t('demoTelemetryNote')}</p>}
          {telemetry.values.length ? telemetry.values.map((value) => {
            // C19-N10: Translate valve_state / pump_state raw values with static keys
            let displayValue = value.value;
            if (value.key === 'valve_state') {
              displayValue = value.value === 'open' ? t('valveState.open') : value.value === 'closed' ? t('valveState.closed') : t('valveState.unknown');
            } else if (value.key === 'pump_state') {
              displayValue = value.value === 'on' ? t('pumpState.on') : value.value === 'off' ? t('pumpState.off') : t('pumpState.unknown');
            }
            return (
            <div className="dt-connected-item" key={value.key}>
              <Radio size={13} /> {value.labelKey ? t(`telemetryLabels.${value.labelKey}`, value.label) : value.label} <small>{displayValue}</small>
            </div>
            );
          }) : <p className="dt-muted">{t(`statusLabel.${telemetry.fallbackKey}`, telemetry.fallbackKey)}</p>}
          {telemetry.source === 'demo' && telemetry.demoAssumptions?.length ? (
            <section className="dt-detail-subsection">
              <h5>{t('demoAgronomicAssumptions')}</h5>
              <ul>{telemetry.demoAssumptions.map((item) => (
                <li key={item.key}>{t(item.key, item.params as Record<string, unknown>)}</li>
              ))}</ul>
            </section>
          ) : null}
          {telemetry.source === 'demo' && telemetry.history ? (
            <section className="dt-detail-subsection">
              <h5>{t('historySummary')}</h5>
              <dl className="dt-detail-grid">
                <dt>{t('lastIrrigation')}</dt><dd>{formatDateTime(new Date(telemetry.history.lastIrrigationEvent))}</dd>
                <dt>{t('avgPressure')}</dt><dd>{telemetry.history.averagePressureBar} bar</dd>
                <dt>{t('waterToday')}</dt><dd>{telemetry.history.waterDeliveredTodayM3} m³</dd>
                <dt>{t('soilTrend')}</dt><dd>{t(`soilMoistureTrend.${telemetry.history.soilMoistureTrend}`, telemetry.history.soilMoistureTrend)}</dd>
                <dt>{t('lastUpdate')}</dt><dd>{formatDateTime(new Date(telemetry.history.lastUpdateTime))}</dd>
              </dl>
            </section>
          ) : null}
        </>
      )}
    </section>
  );
};

const DetailsPanel: React.FC<{
  selection: Selection | null;
  equipment: Equipment[];
  sensors: Sensor[];
  links: HydraulicLink[];
  telemetryByEquipmentId?: Record<string, EquipmentTelemetry>;
  onClose: () => void;
}> = ({ selection, equipment, sensors, links, telemetryByEquipmentId = {}, onClose }) => {
  const { t } = useTranslation('digitalTwin');

  if (!selection) return (
    <aside className="dt-details dt-details--empty">
      <Radio size={28} />
      <h3>{t('inspectNetwork')}</h3>
      <p>{t('inspectNetworkHint')}</p>
    </aside>
  );

  if (selection.kind === 'block') return (
    <aside className="dt-details">
      <button className="dt-close" onClick={onClose} aria-label={t('closePanel')}><X size={17} /></button>
      <span className="dt-detail-eyebrow">{t('irrigationBlock')}</span>
      <h3>{selection.value.name}</h3>
      <BlockDetails block={selection.value} />
    </aside>
  );

  const item = selection.value;
  const linkedSensors = sensors.filter((sensor) => sensor.equipment_id === item.id);
  const upstream = links.filter((link) => link.target_equipment_id === item.id)
    .map((link) => equipment.find((candidate) => candidate.id === link.source_equipment_id)).filter(Boolean) as Equipment[];
  const downstream = links.filter((link) => link.source_equipment_id === item.id)
    .map((link) => equipment.find((candidate) => candidate.id === link.target_equipment_id)).filter(Boolean) as Equipment[];

  return (
    <aside className="dt-details">
      <button className="dt-close" onClick={onClose} aria-label={t('closePanel')}><X size={17} /></button>
      <span className="dt-detail-eyebrow">{item.type.replace(/_/g, ' ')}</span>
      <h3>{item.name}</h3>
      <span className={`dt-detail-status is-${item.status}`}>{item.status}</span>
      <TelemetryDetails telemetry={telemetryByEquipmentId[item.id]} />
      <ValueList title={t('configuration')} value={item.configuration} />
      <ValueList title={t('hydraulics')} value={item.hydraulic_parameters} />
      <ValueList title={t('electrical')} value={item.electrical_parameters} />
      <ValueList title={t('communication')} value={item.communication_parameters} />
      <section className="dt-detail-section">
        <h4>{t('linkedSensors', { count: linkedSensors.length })}</h4>
        {linkedSensors.length ? linkedSensors.map((sensor) => (
          <div className="dt-connected-item" key={sensor.id}><Radio size={13} /> {sensor.name} <small>{sensor.unit}</small></div>
        )) : <p className="dt-muted">{t('noLinkedSensor')}</p>}
      </section>
      <section className="dt-detail-section dt-connections">
        <h4>{t('connections')}</h4>
        {upstream.map((connected) => <div className="dt-connected-item" key={connected.id}><ArrowDownToLine size={13} /> {connected.name}</div>)}
        {downstream.map((connected) => <div className="dt-connected-item" key={connected.id}><ArrowUpFromLine size={13} /> {connected.name}</div>)}
        {!upstream.length && !downstream.length && <p className="dt-muted">{t('noConnections')}</p>}
      </section>
    </aside>
  );
};

export default DetailsPanel;
